/**
 * IICRC loss-risk ladder - ONE derivation, ONE ordering (WEB-13, WEB-16).
 *
 * The classification editor used to derive `riskLevel` inline and the loss
 * card compared the result against a second string literal ('High'). The
 * writer emits four levels; the badge knew one of them, so a Category 3 /
 * Class 3 job - 'Critical', the worst the ladder produces - was the only job
 * showing no risk badge. Readers must rank against this ordering, never
 * against a literal, so a level added here is elevated (or not) by position.
 *
 * GamutIOS re-derives the same ladder in
 * `Services/Networking/FirebaseService+Jobs.swift` and both clients write it
 * to the same field. Agreement between the two is WEB-16's open work; this
 * file is the web's single authoring of it.
 */

/** Risk levels the ladder writes, least to most severe. */
export const LOSS_RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical'] as const;
export type LossRiskLevel = (typeof LOSS_RISK_LEVELS)[number];

/** Position in the ladder, or -1 for a value outside it ('Unknown', '', undefined). */
export function lossRiskRank(level: string | null | undefined): number {
    return level ? LOSS_RISK_LEVELS.indexOf(level as LossRiskLevel) : -1;
}

/**
 * True for High and anything above it. An unknown value ranks below the
 * ladder and is never elevated - a badge is a positive claim, and an
 * unrecognised level is not evidence for one.
 */
export function isElevatedLossRisk(level: string | null | undefined): boolean {
    return lossRiskRank(level) >= lossRiskRank('High');
}

/** The IICRC category/class to risk-level ladder. Mirrored on iOS (see header). */
export function deriveLossRiskLevel(category: number, cls: number): LossRiskLevel {
    if (category === 3 && cls >= 3) return 'Critical';
    if (category === 3 || (category === 2 && cls >= 3)) return 'High';
    if (category === 2 || cls >= 3) return 'Medium';
    return 'Low';
}
